import { Component, Vue, Prop } from 'vue-property-decorator';
import QuestionnaireService from '@/services/questionnaire.service';
import { QuestionnaireDTO } from '@/models/QuestionnaireDTO';
import DoctorAutoComplete from '@/components/inputs/DoctorAutoComplete/DoctorAutoComplete.vue';
import LocalityAutoComplete from '@/components/inputs/LocalityAutoComplete/LocalityAutoComplete.vue';
import InputText from '@/components/inputs/InputText/InputText.vue';
import InputTel from '@/components/inputs/InputTel/InputTel.vue';
import InputCheckbox from '@/components/inputs/InputCheckbox/InputCheckbox.vue';
import { DoctorDTO } from '@/models/DoctorDTO';
import StepStatus from '@/components/StepStatus/StepStatus.vue';
import RenounceButton from '@/components/RenounceButton/RenounceButton.vue';

@Component({
  components: {
    DoctorAutoComplete,
    LocalityAutoComplete,
    InputText,
    InputTel,
    InputCheckbox,
    StepStatus,
    RenounceButton,
  },
})
export default class QuestDoctor extends Vue {
  @Prop({ required: false, default: false }) public isRecap!: boolean;
  public partId: number | undefined;
  public questionnaire: QuestionnaireDTO = {} as QuestionnaireDTO;
  public errorMessage: string = '';
  public error: boolean = false;
  public noDoctorInList: boolean = false;
  public noDoctorOtherInList: boolean = false;
  public doctorDefault:DoctorDTO = {
    doctorId: -10,
    doctorName: this.$i18n.t('questionnaire.doctor.other'),
    doctorAddress: '',
    doctorAddressId: -10
  } as DoctorDTO
  public noDoctorDefault:DoctorDTO = {
    doctorId: -1,
    doctorName: this.$i18n.t('questionnaire.doctor.noDoctor'),
    doctorAddress: '',
    doctorAddressId: -1
  } as DoctorDTO

  /**
   * Get an array of all the property names from the questionnaire data in lowercase
   */
  get instanceFields(): string[] {
    if (this.$store.state.questionnaire.doctor) {
      let fields: string[] = [];
      Object.keys(this.questionnaire.doctor).forEach(e => fields.push(e.toLowerCase()));
      return fields;
    }
    return [];
  }
  
  /**
   * Get the errors returned by the API corresponding to the questionnaire data properties
   */
  get errors(): string[] {
    if (this.$store.state.questionnaireValidationErrors) {
      const obj = this.$store.state.questionnaireValidationErrors;
      let errorFields: string[] = [];
      Object.keys(obj).forEach((e) => {
        if (this.instanceFields.includes(e.toLowerCase())) {
          errorFields.push(obj[e].errors);
        }
      });
      return errorFields;
    }

    return [];
  }

  protected mounted() {
    this.partId = Number(this.$route.params.partId);
    // this.fetchQuestionnaire(this.partId);
    this.questionnaire = this.$store.state.questionnaire;

    // Set the value when doctor not in list
    if (this.questionnaire.doctor.doctor === null || 
      (this.questionnaire.doctor.doctor !== null && this.questionnaire.doctor.doctor.doctorId === -10)) {
      if (this.questionnaire.doctor.doctorLastname) {
        this.onNoDoctorInList(true);
      }
    }

    if (this.questionnaire.doctor.doctorOther === null ||
      (this.questionnaire.doctor.doctorOther !== null && this.questionnaire.doctor.doctorOther.doctorId === -10)) {
      if (this.questionnaire.doctor.doctorOtherLastname) {
        this.onNoDoctorOtherInList(true);
      }
    }

    // Check questionnaire status : if this part is valid, and we have no doctor -> select "No Doctor"
    if (this.questionnaire.doctor.isValidated && !this.noDoctorInList) {
      if (this.questionnaire.doctor.doctor === null || 
        (this.questionnaire.doctor.doctor !== null && this.questionnaire.doctor.doctor.doctorId === -1)) {
          this.questionnaire.doctor.doctor = this.noDoctorDefault;
        }
    }

    if (this.questionnaire.doctor.isValidated && !this.noDoctorOtherInList) {
      if (this.questionnaire.doctor.doctorOther === null ||
        (this.questionnaire.doctor.doctorOther !== null && this.questionnaire.doctor.doctorOther.doctorId === -1)) {
          this.questionnaire.doctor.doctorOther = this.noDoctorDefault;
        }
    }
  }

  /**
   * Fetch the questionnaire data with the service
   * @param partId The participant ID
   */
  protected fetchQuestionnaire(partId: number) {
    QuestionnaireService.fetchQuestionnaire(partId, 'doctor')
      .then((response) => {
        this.questionnaire = response as QuestionnaireDTO;
        this.$store.commit('setQuestionnaireDoctor', response.doctor);

        if (this.questionnaire.doctor.doctorLastname && !this.questionnaire.doctor.doctor) {
          this.noDoctorInList = true;
        }
        if (this.questionnaire.doctor.doctorOtherLastname && !this.questionnaire.doctor.doctorOther) {
          this.noDoctorOtherInList = true;
        }
      });
  }

  /**
   * If checked, clears the doctor data
   * @param checked True if the checkbox is checked, false otherwise
   */
  protected onNoDoctorInList(checked: boolean) {
    this.noDoctorInList = checked;

    // reinitialize data about the doctor
    this.questionnaire.doctor.doctor = checked ? this.doctorDefault : null;
  }

  /**
   * If checked, clears the other doctor data
   * @param checked True if the checkbox is checked, false otherwise
   */
  protected onNoDoctorOtherInList(checked: boolean) {
    this.noDoctorOtherInList = checked;

    // reinitialize data about the doctor
    this.questionnaire.doctor.doctorOther = checked ? this.doctorDefault : null;
  }

  protected toDto(quest: QuestionnaireDTO): QuestionnaireDTO {
    let questClone = JSON.parse(JSON.stringify(quest));

    // A doctor was chosen in the dropdown, so clear manual data of the doctor
    if (!this.noDoctorInList && quest.doctor.doctor !== null) {
      questClone.doctor.doctorFirstname = null;
      questClone.doctor.doctorLastname = null;
      questClone.doctor.doctorAddress = null;
      questClone.doctor.doctorLocality = null;
      questClone.doctor.doctorPhone = null;

      // In case "No doctor" was chosen, clear the doctor object
      if (quest.doctor.doctor.doctorId < 0) {
        questClone.doctor.doctor = null;
      }
    }

    if (!this.noDoctorOtherInList && quest.doctor.doctorOther !== null) {
      questClone.doctor.doctorOtherFirstname = null;
      questClone.doctor.doctorOtherLastname = null;
      questClone.doctor.doctorOtherAddress = null;
      questClone.doctor.doctorOtherLocality = null;
      questClone.doctor.doctorOtherPhone = null;

      // In case "No doctor" was chosen, clear the doctor object
      if (quest.doctor.doctorOther.doctorId < 0) {
        questClone.doctor.doctorOther = null;
      }
    }

    // "Doctor not in list was checked" so clear the doctor object
    if (this.noDoctorInList) {
      questClone.doctor.doctor = null;
    }

    if (this.noDoctorOtherInList) {
      questClone.doctor.doctorOther = null;
    }

    return questClone;
  }

  protected async onSubmit(event: any) {
    const quest = this.toDto(this.questionnaire);
    await QuestionnaireService.updateQuestionnaire(quest, 'doctor')
      .then(() => {
        this.questionnaire.doctor.isValidated = true;
        this.$store.commit('setQuestionnaireDoctor', this.questionnaire.doctor);
        if (event.submitter.id === 'btn-continue') {
          this.$router.push({
            name: 'questions',
            params: {
              screening: this.$route.params.screening,
              partId: String(this.partId),
            },
          });
        } else {
          this.$router.push({
            name: 'overview',
            params: {
              screening: this.$route.params.screening,
              partId: String(this.partId),
            },
          });
        }
      });
  }
}

